import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { RootState } from '.';
import api from '../api/BlancoClient';
import { PersistentData, PersistentUserReactionState } from '../lib/PersistentData';
import { addReaction } from '../lib/reactionHistoryManipulator';
import { persistUserReactionState } from '../lib/storage';
import { ReactionItem, SelectedReactionItem } from '../models/ReactionItem';
import { actions as userActions, getFreshToken, UserState } from './user';
import { checkErrorType } from './utils/helper';
import { RequestState } from './utils/RequestState';

type ReactionErrorType = 'unstable-network' | 'unknown';
export type ReactionPostErrorType = ReactionErrorType | 'im-offline' | 'rate-limit-exceeded';

export interface ReactionState {
  reactionItems?: RequestState<ReactionItem[], ReactionErrorType>;
  userReactionStates?: { [userId: string]: PersistentUserReactionState };
  sendStatus?: RequestState<Record<string, unknown>, ReactionPostErrorType>;
  selectedReaction?: SelectedReactionItem;
}

const initialState: ReactionState = {};

const slice = createSlice({
  name: 'reaction',
  initialState,
  reducers: {
    persistentDataRestored: (state, { payload }: PayloadAction<PersistentData>) => {
      state.userReactionStates = payload.userReactionStates;
    },

    getReactions: state => {
      state.reactionItems = { state: 'pending' };
    },

    getReactionsSucceeded: (state, { payload }: PayloadAction<ReactionItem[]>) => {
      state.reactionItems = { state: 'ok', result: payload };
    },

    getReactionsFailed: (state, { payload }: PayloadAction<ReactionErrorType>) => {
      state.reactionItems = { state: 'error', error: payload };
    },

    selectReaction: (state, { payload }: PayloadAction<SelectedReactionItem>) => {
      state.selectedReaction = payload;
    },

    postReaction: (state, _: PayloadAction<ReactionItem>) => {
      state.sendStatus = { state: 'pending' };
    },

    postReactionSucceeded: (
      state,
      {
        payload
      }: PayloadAction<{ userId: string; history: PersistentUserReactionState['history'] }>
    ) => {
      const { userId, history } = payload;
      if (!state.userReactionStates) {
        state.userReactionStates = { [userId]: { history: [] } };
      }
      const current = state.userReactionStates[userId];

      state.sendStatus = { state: 'ok', result: {} };
      state.selectedReaction = undefined;
      state.userReactionStates[userId] = {
        ...current,
        history
      };
    },

    postReactionFailed: (state, { payload }: PayloadAction<ReactionPostErrorType>) => {
      state.sendStatus = { state: 'error', error: payload };
    },

    clearSelectedReaction: state => {
      state.selectedReaction = undefined;
    },

    clearPostError: state => {
      state.sendStatus = undefined;
    },
    resetReactionItems: state => {
      state.reactionItems = undefined;
    }
  }
});

export default slice.reducer;

const {
  persistentDataRestored,
  getReactions,
  selectReaction,
  postReaction,
  clearSelectedReaction,
  clearPostError,
  resetReactionItems,
  ...internalActions
} = slice.actions;

export const actions = {
  persistentDataRestored,
  getReactions,
  selectReaction,
  postReaction,
  clearSelectedReaction,
  clearPostError,
  resetReactionItems
};

const {
  getReactionsSucceeded,
  getReactionsFailed,
  postReactionSucceeded,
  postReactionFailed
} = internalActions;

// Sagas

export function* reactionSaga() {
  yield takeLatest(getReactions.type, getReactionsSaga);
  yield takeLatest(postReaction.type, postReactionSaga);
}

function* getReactionsSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const userState: UserState = yield select((state: RootState) => state.user);
    const { language } = userState;
    if (!language) {
      // 復帰不可能のエラー
      throw new Error();
    }

    const { emoticons }: { language: string; emoticons: ReactionItem[] } = yield call(
      api.getReactions,
      baid,
      language
    );

    if (emoticons.length === 0) {
      // リアクションが0件の時はエラーとして扱う
      throw new Error();
    }

    yield put(getReactionsSucceeded(emoticons));
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getReactionsFailed('unstable-network'));
    } else {
      yield put(getReactionsFailed('unknown'));
    }
  }
}

function* postReactionSaga(action: { type: typeof postReaction.type; payload: ReactionItem }) {
  try {
    const baid: string = yield call(getFreshToken);
    const userState: UserState = yield select((state: RootState) => state.user);
    const reactionState: ReactionState = yield select((state: RootState) => state.reaction);
    const { selectedUserId: userId } = userState;
    const { userReactionStates } = reactionState;
    if (!userId) {
      // 復帰不可能のエラー
      throw new Error();
    }
    let userReactionState: PersistentUserReactionState;
    if (!userReactionStates || !userReactionStates[userId]) {
      userReactionState = { history: [] };
    } else {
      userReactionState = userReactionStates[userId];
    }
    const { history } = userReactionState;
    const historyResArray = addReaction(history, action.payload.label);
    yield call(api.postReaction, baid, action.payload.label);
    yield call(persistUserReactionState, userId, historyResArray);
    yield put(postReactionSucceeded({ userId, history: historyResArray }));
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(postReactionFailed('unstable-network'));
    } else {
      const { code } = error.response.data;
      const reactionErrorType = getReactionErrorType(code);
      yield put(postReactionFailed(reactionErrorType));
    }
  }
}

function getReactionErrorType(code?: string) {
  switch (code) {
    case '1001':
      return 'im-offline';
    case '3007':
      return 'rate-limit-exceeded';
    default:
      return 'unknown';
  }
}
